"use client";

import Sidebar from "./SideBar/Sidebar";
import Navbar from "./Navbar/Navbar";
import Footer from "./Footer/Footer";
import { useRouter } from "next/navigation";
import { SessionProvider, signOut, useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import { usePathname } from 'next/navigation';
import { CookieGuard } from './CookieGuard/CookieGuard';
import { useCookieConsent } from "@/hooks/animation/useCookieConsent";
import { Toaster } from "react-hot-toast";
import MessageNotification from "./notifications/MessageNotification";
import OverlayActions from "./overlay/OverlayActions";
import { OverlayActionsProvider } from "./overlay/OverlayActionsContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { UnreadCountProvider } from "@/hooks/contexts/UnreadCountContext";


// Composant parent place le SessionProvider
export default function ClientLayout({ children }: { children: React.ReactNode }) {
  return (
    <SessionProvider>
      <CookieGuard>
        <OverlayActionsProvider>
          <ClientLayoutContent>{children}</ClientLayoutContent>
        </OverlayActionsProvider>

      </CookieGuard>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          success: { style: { background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" } },
          error: { style: { background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" } },
          loading: { style: { background: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe" } },
        }}
      />
    </SessionProvider>
  );
}

// Composant enfant on peut utiliser tous les hooks liés à la session
function ClientLayoutContent({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const { data: session, status } = useSession()
  const { needsConsent } = useCookieConsent();
  const router = useRouter();

  const isAuthenticated = !!session;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };



  const handleSearchSubmit = () => {
    if (searchValue.trim()) {
      router.push(`/dossiers/search/${encodeURIComponent(searchValue)}`);
    }
  };

  const pathname = usePathname();

  const isMessagesPage = pathname?.startsWith('/messages') || false;

  const isMobile = useIsMobile();



  // Ne pas afficher l'interface si les cookies ne sont pas acceptés
  const shouldShowAuthenticatedInterface = isAuthenticated && !needsConsent;



  // ✅ Le useEffect pour la déconnexion automatique 
  useEffect(() => {
    const interval = setInterval(() => {
      if (session && new Date().getTime() > new Date(session.expires).getTime()) {
        signOut({ callbackUrl: "/signin" });
      }
    }, 60000); // Vérifie toutes les minutes

    return () => clearInterval(interval);
  }, [session?.expires]);



  return (
    <UnreadCountProvider>
      <div className="flex flex-col min-h-screen">
        {shouldShowAuthenticatedInterface && (
          <Sidebar
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        )}
        <div
          style={{
            marginLeft: !isMobile && shouldShowAuthenticatedInterface && sidebarOpen ? 240 : 0,
            transition: 'margin-left 0.1s ease-in-out'
          }}
        >
          <header>
            {shouldShowAuthenticatedInterface && (
              <Navbar
                sidebarOpen={sidebarOpen}
                onMenuClick={() => setSidebarOpen((open) => !open)}
                searchValue={searchValue}
                onSearchChange={handleSearchChange}
                onSearchSubmit={handleSearchSubmit}
              />
            )}
          </header>
          <div className="flex flex-col">
            <main
              className={shouldShowAuthenticatedInterface ? "main" : "main main-auth"}
              style={{ minHeight: "calc(100vh )" }}
            >
              {children}
            </main>
            {shouldShowAuthenticatedInterface && <Footer />}
          </div>
        </div>

        {/* Notification de messages, gérée par le composant (affichée une seule fois après authentification) */}
        {shouldShowAuthenticatedInterface && (
          <MessageNotification disabled={isMessagesPage} />
        )}

        {/* Bouton Overlay affiché uniquement si utilisateur connecté ET cookies acceptés */}
        {shouldShowAuthenticatedInterface &&

          <OverlayActions />


        }
      </div>
    </UnreadCountProvider>

  );
}